import { useCallback } from 'react';
import { css } from '@emotion/react';
import { PaymentPlanModel, PaymentPlanPerson } from '@frontend/api-payment-plans';
import { PersonQueries } from '@frontend/api-person';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions as ContactActions } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Avatar, Heading, Text } from '@frontend/design-system';
import { useQueryPersonPaymentPlans } from '../../../hooks/use-payment-plan-search';
import { TextDropdown } from '../../Shared/text-dropdown';

export const PersonDetails = ({
  person,
  paymentPlanId,
  locationId,
}: {
  person?: PaymentPlanPerson;
  paymentPlanId: string;
  locationId?: string;
}) => {
  const { personPaymentPlans } = useQueryPersonPaymentPlans(person?.id);
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const { data: personImg } = PersonQueries.useGetPersonImage(person?.id ?? '', {
    enabled: !!person?.id,
  });

  const personName = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

  const { setPersonId } = useContactPanelShallowStore('setPersonId');

  const onOpenUserDrawer = useCallback(() => {
    if (!person) {
      return;
    }
    setPersonId(person.id);
  }, [person]);

  return (
    <div
      css={css`
        display: flex;
        flex-flow: row;
        gap: ${theme.spacing(2)};
        margin: ${theme.spacing(2, isMobile ? 0 : 1)};
        align-items: center;
      `}
    >
      <Avatar onClick={onOpenUserDrawer} src={personImg} name={personName} size={isMobile ? 'small' : 'xl'} />

      {isMobile ? (
        <MobilePersonDetail person={person} locationId={locationId} />
      ) : (
        <DesktopPersonDetail person={person} paymentPlanId={paymentPlanId} personPaymentPlans={personPaymentPlans} />
      )}
    </div>
  );
};

const DesktopPersonDetail = ({
  person,
  paymentPlanId,
  personPaymentPlans,
}: {
  person?: PaymentPlanPerson;
  paymentPlanId: string;
  personPaymentPlans: PaymentPlanModel[];
}) => {
  const { t } = useTranslation('payments');
  return (
    <div>
      <Text
        size='large'
        weight='bold'
        css={css`
          font-size: ${theme.fontSize(20)};
          width: 350px;
          padding-left: ${theme.spacing(0.5)};
        `}
      >
        {person?.firstName} {person?.lastName}
      </Text>
      <TextDropdown
        paymentPlanId={paymentPlanId}
        trackingId='pay-portal-paymentplan-persondetails'
        label={t('Other payment plans')}
        options={personPaymentPlans}
      />
    </div>
  );
};

const MobilePersonDetail = ({ person, locationId }: { person?: PaymentPlanPerson; locationId?: string }) => {
  const { t } = useTranslation('payments');
  const personName = `${person?.firstName || ''} ${person?.lastName || ''}`.trim();

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
      }}
    >
      <Heading level={2}>{personName}</Heading>
      <AdaptoActions
        enableCloseEvent
        iconColor='inherit'
        variant='icon-buttons'
        css={{
          display: 'flex',
          flex: 1,
          justifyContent: 'flex-end',
        }}
      >
        <ContactActions.Call
          label={t('Call')}
          context={{
            personId: person?.id,
            locationId,
          }}
        />
        <ContactActions.Message
          label={t('Message')}
          context={{
            personId: person?.id,
            threadGroupId: locationId,
            groupIds: locationId ? [locationId] : [],
          }}
        />
      </AdaptoActions>
    </div>
  );
};
